import { LibraryFileIcon } from './LibraryFileIcon.js';
import { LibraryShare } from './LibraryShare.js';
import { useEffect, useRef, useState } from 'react';
import { collectPageKeys, applySelectedBatches } from '../../lib/bulkSelection.js';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  FilePenLine,
  FileText,
  Folder,
  Paperclip,
  Pencil,
  Plus,
  ScanLine,
  Share2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { libraryFolderSchema, LIMITS } from '@worki/shared';
import type { LibraryDocument, LibraryFolder, Paginated } from '@worki/shared';
import { handheld } from '@platform';
import { api, authedFetch, buildQuery, RequestError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { useDebounced } from '../../lib/useDebounced.js';
import { useIsPhone } from '../../lib/useIsPhone.js';
import { useLongPress } from '../../lib/useLongPress.js';
import { currentLocale } from '../../i18n.js';
import { formatDate } from '../../lib/format.js';
import { Button, ErrorNotice, Input, Select } from '../../components/ui.js';
import { SelectionActionBar } from '../../components/SelectionActionBar.js';
import { AnchoredMenu } from '../../components/AnchoredMenu.js';
import { Modal, ConfirmDialog } from '../../components/Modal.js';
import { ItemActions } from '../../components/ItemActions.js';
import { QuickScanDialog } from '../../components/QuickScan.js';
import { useToast } from '../../components/Toast.js';
import { fileSize, LibraryCombineDialog, libraryUpload } from './LibraryCombine.js';
import { canEditPages, LibraryEditor } from './LibraryEditor.js';
import { LibrarySelfSign } from './LibrarySelfSign.js';
import { LibraryPreview } from './LibraryPreview.js';
import { browserShareHost, shareFile, shareMerged } from './shareDocument.js';

/**
 * LIB — "my documents".
 *
 * What the owner keeps, as opposed to what they have sent. A permit, a price
 * list, a standard contract, the waiver every customer signs: things that were
 * being hunted for in a phone's gallery every time somebody asked, and that are
 * now filed once and reached in two taps.
 *
 * Filing is not sending. A document goes out from here by being handed to the
 * signing screen, which is still the one place a document for signature is made
 * — so the send has a number, a link and a history exactly like every other.
 *
 * 1.45 — and folders, for the owner whose list has grown past a screen: the
 * menu beside the search box narrows the list to one, a document is moved into
 * one from its row menu, and one that is added while a folder is chosen is filed
 * in it. A folder is only a filing: deleting one deletes no document.
 */

const PAGE_SIZE = 25;
const uploadKeys = new WeakMap<File, string>();
function operationKeyFor(files: File[]): string {
 const first=files[0];
 if (!first) return crypto.randomUUID();
 let key=uploadKeys.get(first);
 if (!key) { key=crypto.randomUUID(); uploadKeys.set(first,key); }
 return key;
}

/** One row of the folder menu. */
const menuItem = 'w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-start transition-colors';

/**
 * What the folder menu files a refused new folder under, in the one slot it keeps
 * for a refusal. No folder's id is this.
 */
const NEW_FOLDER = 'new';

/** `POST /bulk/delete` applies row by row and names the rows it refused. */
interface BulkResult {
  deleted: number;
  failed: string[];
}

const ACCEPT =
  'application/pdf,.pdf,image/jpeg,image/png,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function LibraryList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const phone = useIsPhone();
  /**
   * 1.58 — whether a document on paper is scanned with a camera. A phone is held
   * up to the page; the Android app is one at any width, since a phone turned on
   * its side is wider than the breakpoint and still has its camera.
   */
  const camera = phone || handheld;
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  /** Which page of the list is on screen; see the pager under the rows. */
  const [page, setPage] = useState(1);
  const [scanning, setScanning] = useState(false);
  /** 1.46 — how many documents the last upload added, until it is closed or another begins. */
  const [justAdded, setJustAdded] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [renaming, setRenaming] = useState<LibraryDocument | null>(null);
  const [title, setTitle] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<LibraryDocument | null>(null);
  const [sharing,setSharing]=useState<{ids?:string[];folderId?:string;title:string}|null>(null);
  const [selfSigning, setSelfSigning] = useState<LibraryDocument | null>(null);
  const [previewing, setPreviewing] = useState<LibraryDocument | null>(null);
  /** A folder id, or empty for every document. Only the folder menu sets it. */
  const [folderId, setFolderId] = useState('');
  /** The folder whose bin was pressed in the folder menu, until it is answered. */
  const [folderToDelete, setFolderToDelete] = useState<LibraryFolder | null>(null);
  /** The document whose "העברה לתיקייה" was pressed, until a folder is picked. */
  const [moving, setMoving] = useState<LibraryDocument | null>(null);
  /** 1.44 — the document whose pencil was pressed, while its pages are open for editing. */
  const [editing, setEditing] = useState<LibraryDocument | null>(null);
  /** Section 9 — files picked together, while the screen that makes them one document is open. */
  const [combining, setCombining] = useState<File[] | null>(null);
  /*
   * LIB — several at once, asked for by holding one.
   *
   * There is no button for it, the way there is none on the invoice list: once
   * the hold is known it is a chip that never gets pressed, and a screen that
   * is mostly one document at a time does not need a mode switch standing on it
   * all day. `selectMode` is kept beside the selection so emptying the ticks
   * does not drop out of the mode mid-selection.
   */
  const [selectMode, setSelectMode] = useState(false);
  const [selection, updateSelection] = useState<Set<string>>(new Set());
  const selectionEpoch = useRef(0);
  const selectionReadContext = useRef('');
  const setSelection = (next: Parameters<typeof updateSelection>[0]) => { selectionEpoch.current++; updateSelection(next); };
  useEffect(() => () => { selectionEpoch.current++; }, []);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const pickRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLDivElement>(null);

  const longPress = useLongPress((id) => {
    setSelectMode(true);
    setSelection(new Set([id]));
  });

  const toggleSelect = (id: string) => {
    if (!selection.has(id) && selection.size >= 10000) { toast.error(t('common.bulkSelectionLimit', { count: 10000 })); return; }
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const leaveSelection = () => {
    setSelection(new Set());
    setSelectMode(false);
  };

  const debounced = useDebounced(search, 300);
  selectionReadContext.current = JSON.stringify([search, debounced, folderId, page]);
  const selectAllMatching = useMutation({
    mutationFn: async (capture: { epoch: number; context: string; q: string; folderId: string }) => collectPageKeys(async (page) => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) throw new Error('Selection cancelled');
      const result = await api.get<Paginated<LibraryDocument>>(`/library${buildQuery({ q: capture.q, folderId: capture.folderId, page, pageSize: 100 })}`);
      if (result.total > 10000) throw new Error('common.bulkSelectionLimit');
      return result;
    }, (row) => row.id),
    onSuccess: (keys, capture) => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) return;
      setSelection(new Set(keys)); setSelectMode(keys.length > 0);
    },
    onError: (error, capture) => {
      if (capture.epoch === selectionEpoch.current && capture.context === selectionReadContext.current) toast.error(t(error.message === 'common.bulkSelectionLimit' ? error.message : 'errors.network', { count: 10000 }));
    },
  });

  const list = useQuery({
    // `undefined` is left out of a key when it is hashed, so "all documents" is
    // the key the list has always had.
    // The first page is left out of the key in the same way.
    queryKey: ['library', { debounced, folderId: folderId || undefined, page: page > 1 ? page : undefined }],
    queryFn: () =>
      api.get<Paginated<LibraryDocument>>(`/library${buildQuery({ q: debounced, folderId, page, pageSize: PAGE_SIZE })}`),
  });

  // A new search or another folder is a new list, which starts at its first page.
  useEffect(() => {
    setPage(1);
  }, [debounced, folderId]);

  const folders = useQuery({
    queryKey: ['library-folders'],
    queryFn: () => api.get<{ items: LibraryFolder[] }>('/library/folders'),
  });
  const folderList = folders.data?.items ?? [];

  /*
   * The folder that is chosen can go on another device. Once the folders have
   * been read again and it is not among them, the list goes back to every
   * document instead of staying narrowed to a folder that is no longer there.
   */
  useEffect(() => {
    if (folderId && folders.data && !folders.data.items.some((folder) => folder.id === folderId)) setFolderId('');
  }, [folderId, folders.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['library'] });
    // What a folder holds is a fact about the documents, so their counts follow.
    void queryClient.invalidateQueries({ queryKey: ['library-folders'] });
  };

  /*
   * Filing, in one request whatever was picked, and it ends as one document.
   *
   * A file on its own goes up as it is, as it always did: in its own format and
   * under its own name. Several are one document (section 9) — they go up
   * together, in the order the screen put them in and under the name it was
   * given, and the server makes them one PDF or refuses the lot, so a failure
   * never leaves some of them filed. A name is what says a screen sent them: the
   * pre-save screen and the scan's review both send one. Either way it is filed
   * in the folder that is open (see `libraryUpload`).
   */
  const upload = useMutation({
    mutationFn: async ({ files, title, asItIs }: { files: File[]; title?: string; asItIs?: boolean }) => {
      const { path, form } = libraryUpload(files, { title, folderId, asItIs });
      form.append('operationKey', operationKeyFor(files));
      await api.upload<LibraryDocument>(path, form);
      return 1;
    },
    onMutate: () => setJustAdded(0),
    onSuccess: (added) => {
      invalidate();
      // The list is newest first, so what was just added is on the first page.
      setPage(1);
      setJustAdded(added);
      toast.success(t('library.added', { count: added }));
    },
    onError: (cause, { title }) => {
      // A screen that sent a name says the refusal itself, beside its retry and
      // with every file still on it; a toast as well would say it twice.
      if (title !== undefined) return;
      const failure = cause instanceof RequestError ? cause : null;
      toast.error(t(failure?.fields?.file ?? failure?.code ?? 'errors.unknown'));
    },
  });

  /*
   * Section 9 — what was picked, wherever it was picked. One file is filed as it
   * is, with no screen in between. Several are always one document, and first go
   * to the short screen where it is named and its files are put in order.
   */
  const receive = (files: File[]) => {
    if (files.length === 1) upload.mutate({ files });
    else if (files.length > 1) setCombining(files);
  };

  /** The pre-save screen's and the scan's save: one document, the files in their order, under their name. */
  const fileTogether = (files: File[], title: string) => upload.mutateAsync({ files, title });

  const rename = useMutation({
    mutationFn: (input: { id: string; title: string }) =>
      api.patch<LibraryDocument>(`/library/${input.id}`, { title: input.title }),
    onSuccess: () => {
      setRenaming(null);
      invalidate();
      toast.success(t('common.saved'));
    },
    onError: () => toast.error(t('errors.unknown')),
  });

  const remove = useMutation({
    mutationFn: (document: LibraryDocument) => api.del(`/library/${document.id}`),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
      toast.success(t('common.deleted'));
    },
    onError: () => {
      setConfirmDelete(null);
      toast.error(t('errors.unknown'));
    },
  });

  /*
   * One request for the whole selection. The endpoint answers row by row, so a
   * document another device already removed is named rather than failing the
   * batch — and the screen says how many of the ticked rows are still there
   * instead of claiming they all went.
   */
  const removeMany = useMutation({
    mutationFn: async () => {
      selectionEpoch.current++;
      const attempted = [...selection];
      const result = await applySelectedBatches(attempted, async (ids) => {
        const outcome = await api.post<BulkResult>('/library/bulk/delete', { ids });
        return { ok: outcome.deleted, failed: outcome.failed };
      }, LIMITS.maxBulkInvoices);
      return { ...result, attempted };
    },
    onSuccess: (result) => {
      setConfirmBulk(false);
      setSelection((current) => new Set([...current].filter(id => !result.attempted.includes(id) || result.failed.includes(id))));
      invalidate();
      if (result.failed.length > 0) toast.error(t('library.deleteSomeRefused', { count: result.failed.length }));
      else toast.success(t('common.deleted'));
    },
    onError: () => {
      setConfirmBulk(false);
      toast.error(t('errors.unknown'));
    },
  });

  /*
   * 1.45 — a folder made from the folder menu. It settles once the folders have
   * been read again, so the menu shows it the moment its field closes. A refusal,
   * such as a name another folder already has, rejects with the server's code for
   * the menu to show under the field.
   */
  const createFolder = async (name: string) => {
    await api.post<LibraryFolder>('/library/folders', { name });
    await queryClient.invalidateQueries({ queryKey: ['library-folders'] });
  };

  /*
   * 1.45 — a folder renamed in the menu, settling the same way: the row keeps
   * the new name while it is on its way, and a refusal rejects for the menu to
   * show under that row.
   */
  const renameFolder = async (folder: LibraryFolder, name: string) => {
    await api.put<LibraryFolder>(`/library/folders/${folder.id}`, { name });
    await queryClient.refetchQueries({ queryKey: ['library-folders'] }, { cancelRefetch: false });
  };

  /*
   * 1.45 — the bin beside a folder, once the question has been answered. The
   * server takes the documents out of the folder and deletes none of them, so a
   * list narrowed to it goes back to every document, where they now are.
   */
  const removeFolder = useMutation({
    mutationFn: (folder: LibraryFolder) => api.del(`/library/folders/${folder.id}`),
    onSuccess: (_result, folder) => {
      setFolderToDelete(null);
      if (folder.id === folderId) setFolderId('');
      invalidate();
      toast.success(t('common.deleted'));
    },
    onError: () => {
      setFolderToDelete(null);
      toast.error(t('errors.unknown'));
    },
  });

  /*
   * 1.45 — "העברה לתיקייה", or out of its folder when there is none. The toast
   * says where the document went: with a folder chosen it leaves this list, and
   * a document that vanishes without a word reads as one that was lost.
   */
  const move = useMutation({
    mutationFn: (input: { document: LibraryDocument; folder: LibraryFolder | null }) =>
      api.put<LibraryDocument>(`/library/${input.document.id}/folder`, { folderId: input.folder?.id ?? null }),
    onSuccess: (_result, { folder }) => {
      setMoving(null);
      invalidate();
      toast.success(folder ? t('library.movedTo', { name: folder.name }) : t('library.movedOut'));
    },
    onError: (cause) => {
      setMoving(null);
      toast.error(t(cause instanceof RequestError ? cause.code : 'errors.unknown'));
    },
  });

  /*
   * Downloaded through the API rather than from a link, because the file is not
   * public: the route checks the session, so the bytes have to be fetched and
   * handed to the browser rather than pointed at.
   */
  const download = async (row: LibraryDocument) => {
    try {
      await api.download(`/library/${row.id}/file`, row.fileName);
    } catch {
      toast.error(t('errors.unknown'));
    }
  };

  /*
   * 1.44 — "שיתוף": the file itself, fetched through the session as the download
   * is, and handed to the phone's share sheet or to the browser's (see
   * `shareDocument`). It goes under the name "הורדה" saves it as.
   */
  const share = async (row: LibraryDocument) => {
    try {
      const response = await authedFetch(`/library/${row.id}/file`);
      if (!response.ok) throw new Error(String(response.status));
      const file = new File([await response.blob()], row.fileName, { type: row.mime });
      return await shareFile(file, row.title, browserShareHost());
    } catch {
      toast.error(t('errors.unknown'));
    }
  };

  /*
   * In a dialog, not a new tab. Opening a tab is what a phone cannot be
   * trusted with — a pop-up blocker, an embedded browser, or Android's
   * download manager asking without the cookie all turn the tap into nothing.
   * The dialog draws the file itself; see `LibraryPreview`.
   */
  const view = (row: LibraryDocument) => setPreviewing(row);

  const rows = list.data?.items ?? [];

  /*
   * Twenty-five documents a page, and the pager under the rows once there are
   * more, as on the quotes list. Without it the list was the first page and
   * nothing more: a 26th document could be found by searching for it and not by
   * looking. A delete that empties the last page goes back to the one before.
   */
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (list.data && page > pages) setPage(pages);
  }, [list.data, page, pages]);

  /*
   * 1.59 — "שיתוף" on the selection bar. One document is shared exactly as its
   * row menu shares it. Several go out as one PDF: the server merges them in
   * the order the list shows them and that file is handed on the same way, under
   * a name of its own — not a link, and not a file apiece.
   *
   * After it the ticks are gone, as they are after a delete. A share sheet that
   * was only closed is not a share, so then they stay for the next try; a share
   * that failed says so and keeps them too.
   */
  const shareSelected = useMutation({
    mutationFn: async () => {
      if (selection.size > LIMITS.maxBulkInvoices) throw new RequestError(400, 'common.bulkShareLimit');
      selectionEpoch.current++;
      const attempted = [...selection];
      const one = attempted.length === 1 ? rows.find((row) => row.id === attempted[0]) : undefined;
      const outcome = one ? await share(one) : await shareMerged(attempted, t('library.mergedName'), browserShareHost());
      return { outcome, attempted };
    },
    onSuccess: ({ outcome, attempted }) => {
      if (outcome && outcome !== 'dismissed') setSelection((current) => new Set([...current].filter(id => !attempted.includes(id))));
    },
    onError: (cause) => toast.error(t(cause instanceof RequestError ? cause.code : 'errors.unknown', { count: LIMITS.maxBulkInvoices })),
  });

  // Bounded batches avoid one mutating HTTP request for every selected row.
  const moveSelected = useMutation({
    mutationFn: async (target: string) => {
      selectionEpoch.current++;
      const ids = [...selection];
      const result = await applySelectedBatches(ids, async (batch) => {
        const outcome = await api.post<{ moved: number; failed: string[] }>('/library/bulk/folder', { ids: batch, folderId: target === 'none' ? null : target });
        return { ok: outcome.moved, failed: outcome.failed };
      }, LIMITS.maxBulkInvoices);
      const failed = new Set(result.failed);
      return { failed: result.failed, target, succeeded: ids.filter(id => !failed.has(id)) };
    },
    onSuccess: ({ failed, target, succeeded }) => {
      invalidate();
      setSelection((current) => {
        const remaining = new Set(current);
        for (const id of succeeded) remaining.delete(id);
        return remaining;
      });
      if (failed.length) {
        toast.error(t('library.moveSomeRefused', { count: failed.length }));
      } else {
        const folder = folderList.find((entry) => entry.id === target);
        toast.success(folder ? t('library.movedTo', { name: folder.name }) : t('library.movedOut'));
      }
    },
  });

  /*
   * The number on the first row. The rows are numbered as the list shows them —
   * a search numbers what it finds and a delete closes the gap — so the number
   * is a place in the list and not something a document keeps. A page after the
   * first goes on from where the one before it stopped instead of starting at 1
   * again; the server says which page this is.
   */
  const firstNumber = list.data ? (list.data.page - 1) * list.data.pageSize + 1 : 1;

  /*
   * The ways a document gets here, in the order the paperclip lists them: one
   * that already exists as a file, one that is still on paper, and one that
   * does not exist yet and is written. A document for signature is made on the
   * signing screen, so the last goes there, and the address says that it is
   * for writing one. Another way in is one more line.
   *
   * 1.58 — Paper is what a phone's camera is for, and a desktop has none to hold
   * up. There the first two are one line, "העלאת מסמכים": it opens the quick scan
   * without its camera, where files are dropped or chosen. A PDF or a Word file
   * on its own is filed as it is, as this line always did, and several with no
   * photograph among them are one document, named and ordered on the pre-save
   * screen (section 9). A batch with a photograph in it is scanned, documents
   * and all, and comes out as one PDF once its pages have been looked over.
   * The scan is not listed.
   */
  const addWays = [
    { label: t('library.upload'), icon: Upload, run: () => (camera ? pickRef.current?.click() : setScanning(true)) },
    ...(camera ? [{ label: t('scan.title'), icon: ScanLine, run: () => setScanning(true) }] : []),
    { label: t('library.createDigital'), icon: FilePenLine, run: () => navigate('/signing/new?digital=1') },
  ];

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        {/* A plain box with a caret, no magnifier: the placeholder already says
            what typing here does, and the icon was read as a button to press
            before the search would start. */}
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('library.searchPlaceholder')}
            aria-label={t('shell.search')}
            className="w-full h-9 pe-3 ps-8 rounded-lg bg-muted text-sm outline-none placeholder:text-muted-foreground"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t('shell.closeSearch')}
              className="absolute start-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-accent"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* 1.45 — the folders, beside the search box the way the invoices have
            them beside their periods. Another folder is another list, so the
            ticks held on the one before are let go of: what is deleted is what
            is on the screen. */}
        <LibraryFolderFilter
          value={folderId}
          folders={folderList}
          onChange={(id) => {
            setFolderId(id);
            leaveSelection();
          }}
          onCreate={createFolder}
          onRename={renameFolder}
          onDelete={setFolderToDelete}
        />

        {/* One paperclip for every way a document gets here: which one is asked
            for in the menu it opens, rather than by a button of its own each.
            The picker stays outside the menu, which is gone once it closes. */}
        <div ref={addRef} className="flex shrink-0">
          <Button
            variant="primary"
            loading={upload.isPending}
            icon={<Paperclip className="h-4 w-4" aria-hidden />}
            aria-label={t('common.add')}
            aria-haspopup="menu"
            aria-expanded={addOpen}
            onClick={() => setAddOpen((open) => !open)}
          >
            <span className="hidden sm:inline">{t('common.add')}</span>
          </Button>
          <AnchoredMenu open={addOpen} onClose={() => setAddOpen(false)} anchorRef={addRef} align="end" width={240}>
            {addWays.map(({ label, icon: Icon, run }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  run();
                }}
                className="w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2"
              >
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                {label}
              </button>
            ))}
          </AnchoredMenu>
        </div>
        <input
          ref={pickRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="sr-only"
          onChange={(event) => {
            const files = [...(event.target.files ?? [])];
            event.target.value = '';
            receive(files);
          }}
        />
      </div>

      <AddedNotice count={justAdded} onClose={() => setJustAdded(0)} />

      {/* The selection bar, in place only while there is a selection: what can
          be done to several documents at once is share them or delete them, and
          "cancel" is the one way out of the mode. */}
      {selection.size > 0 && (
        <SelectionActionBar count={selection.size} onClear={leaveSelection}>
          <button
            type="button"
            disabled={moveSelected.isPending}
            onClick={() => {
              const combined = new Set([...selection, ...rows.map((row) => row.id)]);
              if (combined.size > 10000) { toast.error(t('common.bulkSelectionLimit', { count: 10000 })); return; }
              setSelection(combined);
            }}
            className="text-xs text-primary hover:underline disabled:opacity-40"
          >
            {t('invoices.selectPageOnly', { count: rows.length })}
          </button>
          <button type="button" disabled={selectAllMatching.isPending || moveSelected.isPending || removeMany.isPending || list.isFetching || search !== debounced}
            onClick={() => selectAllMatching.mutate({ epoch: ++selectionEpoch.current, context: selectionReadContext.current, q: debounced, folderId })}
            className="text-xs text-primary hover:underline disabled:opacity-40">
            {t('invoices.selectAllMatching', { count: list.data?.total ?? 0 })}
          </button>
          <Select
            value=""
            aria-label={t('library.moveToFolder')}
            disabled={moveSelected.isPending}
            onChange={(value) => { if (value) moveSelected.mutate(value); }}
            className="h-8 px-2 bg-muted text-xs w-auto"
          >
            <option value="">{t('library.moveToFolder')}</option>
            <option value="none">{t('library.noFolder')}</option>
            {folderList.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
          </Select>
          {/* 1.59 — before the delete, as in the invoice list's bar: the one of
              the two that is not destructive. An icon, as Nitzan asked for one,
              so its name is for a screen reader and for the tooltip. */}
          <button
            type="button"
            aria-label={t('common.share')}
            title={t('common.share')}
            disabled={selection.size === 0 || shareSelected.isPending || moveSelected.isPending}
            onClick={() => setSharing({ids:[...selection],title:t('library.mergedName')})}
            className="inline-flex h-7 w-7 -my-1 items-center justify-center rounded-md text-primary hover:bg-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Share2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={selection.size === 0 || moveSelected.isPending}
            onClick={() => setConfirmBulk(true)}
            className="text-xs text-destructive hover:underline disabled:opacity-40 disabled:no-underline"
          >
            {t('common.delete')}
          </button>
        </SelectionActionBar>
      )}

      {folderId && total>0 && <Button icon={<Share2 className="h-4 w-4"/>} onClick={()=>setSharing({folderId,title:folderList.find(folder=>folder.id===folderId)?.name??t('library.mergedName')})}>{t('library.shareFolder')}</Button>}
      <div className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.id}
            role="button"
            tabIndex={0}
            onClick={() => {
              // The click a finished hold ends with would unpick the row the
              // hold just picked.
              if (longPress.consumeClick()) return;
              if (selectMode) toggleSelect(row.id);
              else view(row);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') view(row);
            }}
            {...longPress.handlers(row.id)}
            className="glass-card list-tint rounded-lg px-3 py-4 cursor-pointer hover:border-primary/20 transition-colors flex items-center gap-3 select-none [-webkit-touch-callout:none]"
          >
            {selectMode && (
              <input
                type="checkbox"
                checked={selection.has(row.id)}
                onChange={() => toggleSelect(row.id)}
                onClick={(event) => event.stopPropagation()}
                aria-label={t('common.selectRow')}
                className="rounded border-border h-4 w-4 accent-primary shrink-0"
              />
            )}
            <LibraryFileIcon mime={row.mime} />

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{row.title}</p>
              {/* Each fact isolated on its own. As one left-to-right string, the
                  Hebrew "עמודים" pulled the size's number to its far side and the
                  line of every PDF read "5 46 · עמודים KB". In right-to-left
                  order now: the date, the pages, the size. */}
              <p className="text-[11px] text-muted-foreground truncate">
                {[
                  formatDate(row.createdAt, locale),
                  row.pageCount ? t('scan.pageCount', { count: row.pageCount }) : null,
                  fileSize(row.size),
                ]
                  .filter(Boolean)
                  .map((fact, index) => (
                    <span key={index}>
                      {index > 0 && ' · '}
                      <bdi>{fact}</bdi>
                    </span>
                  ))}
              </p>
            </div>

            <span onClick={(event) => event.stopPropagation()} className="shrink-0">
              <ItemActions
                onView={() => view(row)}
                viewLabel={t('common.view')}
                onShare={() => setSharing({ids:[row.id],title:row.title})}
                onMove={() => setMoving(row)}
                onSign={() => setSelfSigning(row)}
                onEdit={() => {
                  // A PDF or a photograph has pages to edit. A Word file has none to
                  // show, so its pencil is still the name.
                  if (canEditPages(row.mime)) {
                    setEditing(row);
                    return;
                  }
                  setRenaming(row);
                  setTitle(row.title);
                }}
                onDownload={() => void download(row)}
                onDelete={() => setConfirmDelete(row)}
              />
            </span>
          </div>
        ))}

        {list.isLoading && <div className="text-center py-8 text-sm text-muted-foreground">{t('common.loading')}</div>}
        {!list.isLoading && rows.length === 0 && (
          <div className="glass-card rounded-xl text-center py-10 px-4 space-y-2">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="text-sm font-medium">{t('library.emptyTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('library.emptyBody')}</p>
          </div>
        )}
      </div>

      <p role="status" className="text-xs text-muted-foreground text-center mt-4">{t('library.resultRange',{from:total?firstNumber:0,to:Math.min(total,firstNumber+rows.length-1),total})}</p>
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 mt-4">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <span className="text-xs text-muted-foreground ltr-nums">
            {t('common.page')} {page} {t('common.of')} {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((current) => current + 1)}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Section 9 — the scan's review is this screen's pre-save screen for
          photographs: it already names the document and orders its pages with
          arrows, so its pages are filed here in one request rather than made a
          PDF there first, and a refusal leaves them in the review for a retry. */}
      <QuickScanDialog
        open={scanning}
        onClose={() => setScanning(false)}
        onSave={fileTogether}
        camera={camera}
        title={camera ? undefined : t('library.upload')}
        onFileAsIs={camera ? undefined : receive}
        readPages={false}
      />

      {/* A file left alone on it, the others taken off, is filed as it is, as one
          file picked alone would have been, under the name typed there. */}
      <LibraryCombineDialog
        files={combining}
        onSave={(files, title) => upload.mutateAsync({ files, title, asItIs: files.length === 1 })}
        onClose={() => setCombining(null)}
      />

      {sharing && <LibraryShare {...sharing} onClose={()=>setSharing(null)}/>}
      {selfSigning && <LibrarySelfSign document={selfSigning} onClose={()=>setSelfSigning(null)} onSaved={value=>{setSelfSigning(null);invalidate();setPreviewing(value);toast.success(t('common.saved'));}}/>}
      <LibraryPreview
        document={previewing}
        onClose={() => setPreviewing(null)}
        onDownload={(row) => void download(row)}
      />

      <LibraryEditor document={editing} onClose={() => setEditing(null)} />

      <Modal
        open={Boolean(renaming)}
        title={t('library.renameTitle')}
        onClose={() => setRenaming(null)}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setRenaming(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={rename.isPending}
              disabled={title.trim() === ''}
              onClick={() => renaming && rename.mutate({ id: renaming.id, title: title.trim() })}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <label className="block text-sm space-y-1">
          <span className="text-muted-foreground">{t('library.titleLabel')}</span>
          <input
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={160}
            className="w-full h-10 px-3 rounded-lg bg-muted text-sm outline-none"
          />
        </label>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('library.deleteTitle')}
        message={t('library.deleteBody')}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
      />

      {/* The same warning, counted: what is about to go is several files, and
          the number is the part worth reading twice. */}
      <ConfirmDialog
        open={confirmBulk}
        title={t('library.deleteManyTitle', { count: selection.size })}
        message={t('library.deleteBody')}
        confirmLabel={t('common.delete')}
        loading={removeMany.isPending}
        onCancel={() => setConfirmBulk(false)}
        onConfirm={() => removeMany.mutate()}
      />

      <MoveToFolderDialog
        document={moving}
        folders={folderList}
        loading={move.isPending}
        onPick={(folder) => moving && move.mutate({ document: moving, folder })}
        onClose={() => setMoving(null)}
      />

      {/* 1.45 — the bin beside a folder. It names the folder and says the
          documents in it stay: the server takes them out of the folder and
          deletes none of them. */}
      <ConfirmDialog
        open={Boolean(folderToDelete)}
        title={t('library.deleteFolder')}
        message={t('library.deleteFolderNamed', {
          name: folderToDelete?.name ?? '',
          count: folderToDelete?.count ?? 0,
        })}
        confirmLabel={t('common.delete')}
        loading={removeFolder.isPending}
        onCancel={() => setFolderToDelete(null)}
        onConfirm={() => folderToDelete && removeFolder.mutate(folderToDelete)}
      />
    </>
  );
}

/**
 * 1.46 — how many documents the last upload added, kept on the screen.
 *
 * The toast says it and is gone in a moment, which is the length of a glance away
 * from the list; this stays above the list until it is closed or the next upload
 * starts. The toast's own words, so that the two cannot say different things.
 */
export function AddedNotice({ count, onClose }: { count: number; onClose: () => void }) {
  const { t } = useTranslation();
  if (count < 1) return null;
  return (
    <p className="flex items-center gap-2 mb-2 rounded-lg bg-success/10 text-success px-3 py-2 text-sm font-medium">
      <Check className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{t('library.added', { count })}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="p-1 -m-1 rounded-md hover:bg-success/10"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </p>
  );
}

/**
 * 1.45 — the folder menu: "כל המסמכים", the folders, and "תיקייה חדשה".
 *
 * A drawn menu and not a `<select>`, as the invoices' is: a folder is renamed and
 * deleted where it is listed, and a native `<option>` holds text and nothing else.
 * Pressing "כל המסמכים" takes the choice away, so the list is every document
 * again, filed or not — filing one leaves it where it was on that list.
 *
 * A pencil renames the folder on its row and a bin asks whether to delete it.
 * Both are always drawn, because a phone has no pointer to hover with.
 */
function LibraryFolderFilter({
  value,
  folders,
  onChange,
  onCreate,
  onRename,
  onDelete,
}: {
  /**
   * A folder id, or empty for every document. Anything that names no folder on
   * the list reads as no folder chosen.
   */
  value: string;
  folders: LibraryFolder[];
  onChange: (id: string) => void;
  /** Settles once the folders are read again; rejects with the server's refusal. */
  onCreate: (name: string) => Promise<unknown>;
  /** Settles once the folders are read again; rejects with the server's refusal. */
  onRename: (folder: LibraryFolder, name: string) => Promise<void>;
  /** Asks whether to delete the folder; nothing goes until that is agreed to. */
  onDelete: (folder: LibraryFolder) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);

  /** The folder whose name is being typed over, and what is typed so far. */
  const [renaming, setRenaming] = useState<{ folder: LibraryFolder; name: string } | null>(null);
  /** A new name sent and not yet read back, kept on its row meanwhile. */
  const [sent, setSent] = useState<{ id: string; name: string } | null>(null);
  /**
   * The server's refusal of a rename, shown under the folder it refused, or of a
   * new folder, shown under its field.
   */
  const [refused, setRefused] = useState<{ id: string; code: string } | null>(null);
  /** The name typed for a new folder, while "תיקייה חדשה" has its field open. */
  const [creating, setCreating] = useState<string | null>(null);
  /** Whether a new folder has been sent and not yet read back. */
  const [making, setMaking] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  /*
   * A rename ends on Enter, on the tick, on Escape, when its field is left, or
   * when the menu closes, and one end can bring on another: Enter and Escape
   * take the field away while it has the focus, and the browser may then report
   * the field as left. Only the first end counts, so a name kept by Enter is not
   * dropped again by the field going.
   */
  const ended = useRef(false);
  /** Whether the menu is open, for a refusal that comes back after it has closed. */
  const shown = useRef(false);

  const nameOf = (folder: LibraryFolder) => (sent?.id === folder.id ? sent.name : folder.name);

  const chosen = folders.find((folder) => folder.id === value);
  const label = chosen ? nameOf(chosen) : t('library.allDocuments');

  const refusal = (failure: unknown) => (failure instanceof RequestError ? failure.code : 'errors.unknown');

  const show = () => {
    shown.current = true;
    setOpen(true);
  };

  /*
   * A name typed over a folder is kept by Enter or the tick, and by nothing else.
   * Leaving the field used to keep it in the invoices, so a name started and then
   * abandoned — the menu closed, another row pressed — became the folder's name,
   * and the old one was gone without anyone having saved anything.
   */
  const endRename = (keep: boolean) => {
    if (!renaming || ended.current) return;
    ended.current = true;
    setRenaming(null);
    const { folder } = renaming;
    const name = renaming.name.trim();
    // An emptied name would only be refused, and the name it already has would
    // change nothing, so neither is sent.
    if (!keep || !name || name === folder.name) return;
    setSent({ id: folder.id, name });
    void onRename(folder, name)
      .catch((failure: unknown) => {
        // Under its folder while the menu is open. Once it has closed there is
        // no row to show it under, and silence would read as a rename that took.
        if (shown.current) setRefused({ id: folder.id, code: refusal(failure) });
        else toast.error(t(refusal(failure)));
      })
      .finally(() => setSent((was) => (was?.id === folder.id ? null : was)));
  };

  const close = () => {
    // Closing the menu over a name being typed puts the old name back, and
    // drops a new folder's name before anything is made. Every way the menu
    // closes comes through here.
    endRename(false);
    setCreating(null);
    shown.current = false;
    setOpen(false);
    setRefused(null);
  };

  const startRename = (folder: LibraryFolder) => {
    // A name still being typed for another folder is dropped, as leaving its
    // field drops it — and dropped now, before this rename begins, so that
    // field going later cannot end this rename instead of its own.
    field.current?.blur();
    // One field at a time, unless a new folder is already on its way.
    if (!making) setCreating(null);
    ended.current = false;
    setRefused(null);
    setRenaming({ folder, name: folder.name });
  };

  const startCreate = () => {
    field.current?.blur();
    setRefused(null);
    setCreating('');
  };

  /*
   * The new folder's field ends as a rename does: Enter or the tick makes the
   * folder, and leaving the field, Escape or closing the menu drops the name. The
   * name is checked as the server checks it, and stays in its field until the
   * server has answered, so a refused name is there to be corrected.
   */
  const endCreate = (keep: boolean) => {
    if (creating === null || making) return;
    if (!keep || !creating.trim()) {
      setCreating(null);
      setRefused(null);
      return;
    }
    const parsed = libraryFolderSchema.safeParse({ name: creating });
    if (!parsed.success) {
      setRefused({ id: NEW_FOLDER, code: parsed.error.issues[0]?.message ?? 'errors.validation' });
      return;
    }
    setRefused(null);
    setMaking(true);
    void onCreate(parsed.data.name)
      .then(() => setCreating(null))
      .catch((failure: unknown) => {
        // Under the field while the menu is open, as a refused rename is.
        if (shown.current) setRefused({ id: NEW_FOLDER, code: refusal(failure) });
        else toast.error(t(refusal(failure)));
      })
      .finally(() => setMaking(false));
  };

  return (
    <>
      <button
        type="button"
        ref={anchor}
        onClick={() => (open ? close() : show())}
        aria-expanded={open}
        aria-label={t('library.folders')}
        className={cn(
          'h-9 px-3 shrink-0 max-w-[45%] inline-flex items-center gap-1.5 rounded-lg text-sm transition-colors',
          chosen ? 'bg-primary/15 text-primary' : 'bg-muted hover:bg-muted/70',
        )}
      >
        {/* The icon carries the heading and the label the answer, so a chosen
            folder's name is what is read back off the control. */}
        <Folder className={cn('h-4 w-4 shrink-0', chosen ? 'text-primary' : 'text-muted-foreground')} />
        <span className="truncate">{label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-60 transition-transform', open && 'rotate-180')} />
      </button>
      <AnchoredMenu open={open} onClose={close} anchorRef={anchor} width={260}>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => {
              onChange('');
              close();
            }}
            className={cn(menuItem, 'hover:bg-muted', !chosen && 'text-primary')}
          >
            {t('library.allDocuments')}
          </button>
          {/* Before the first folder exists the menu would otherwise be a lone
              row that means "everything", which reads as a control that failed
              to load rather than as one with nothing to offer yet. */}
          {folders.length === 0 && (
            <p className="px-3 py-2.5 text-sm text-muted-foreground">{t('library.noFoldersYet')}</p>
          )}
          {folders.map((folder) => {
            const draft = renaming?.folder.id === folder.id ? renaming : null;
            return (
              <div key={folder.id}>
                <div className={cn('flex items-center pe-2', value === folder.id && 'bg-muted/50')}>
                  {draft ? (
                    /* The name, typed over where it stands, with a tick beside
                       it to keep it. */
                    <>
                      <Input
                        ref={field}
                        autoFocus
                        value={draft.name}
                        maxLength={60}
                        enterKeyHint="done"
                        aria-label={t('library.folderName')}
                        onChange={(event) => setRenaming({ folder, name: event.target.value })}
                        onBlur={() => endRename(false)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') endRename(true);
                          if (event.key === 'Escape') endRename(false);
                        }}
                        className="h-9 my-0.5 ms-1 flex-1 min-w-0"
                      />
                      <button
                        type="button"
                        // Pressing the tick would take the focus from the field
                        // before the tick is clicked, and leaving the field puts
                        // the old name back.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => endRename(true)}
                        aria-label={t('common.save')}
                        title={t('common.save')}
                        className="p-1.5 shrink-0 rounded-lg text-primary hover:bg-muted transition-colors"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(folder.id);
                          close();
                        }}
                        className={cn(menuItem, 'flex-1 min-w-0 hover:bg-muted', value === folder.id && 'text-primary')}
                      >
                        <span className="truncate">{nameOf(folder)}</span>
                        <span className="ms-auto shrink-0 text-xs text-muted-foreground ltr-nums">{folder.count}</span>
                      </button>
                      {/* A button of its own, as the bin is and for the same
                          reason: pressing it is not pressing the folder, so it
                          neither chooses the folder nor closes the menu. Held
                          back only while a new name is on its way. */}
                      <button
                        type="button"
                        disabled={sent?.id === folder.id}
                        onClick={() => startRename(folder)}
                        aria-label={t('library.renameFolder')}
                        title={t('library.renameFolder')}
                        className="p-1.5 shrink-0 rounded-lg text-muted-foreground hover:bg-muted transition-colors disabled:opacity-30"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {/* It closes the menu, because the question it asks opens
                          over the screen and the menu would float above it; the
                          screen asks, and deletes nothing unless agreed to. */}
                      <button
                        type="button"
                        disabled={sent?.id === folder.id}
                        onClick={() => {
                          onDelete(folder);
                          close();
                        }}
                        aria-label={t('library.deleteFolder')}
                        title={t('library.deleteFolder')}
                        className="p-1.5 shrink-0 rounded-lg text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
                {refused?.id === folder.id && (
                  <div className="px-3 pb-2">
                    <ErrorNotice code={refused.code} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Under the list and not in it, so a long list of folders never buries
            the way to make another. */}
        <div className="border-t border-border py-1">
          {creating === null ? (
            <button type="button" onClick={startCreate} className={cn(menuItem, 'text-primary hover:bg-muted')}>
              <Plus className="h-4 w-4 shrink-0" />
              {t('library.newFolder')}
            </button>
          ) : (
            <div className="flex items-center pe-2">
              {/* Read-only rather than disabled while the name is on its way: a
                  disabled field gives up the focus, and the keyboard with it. */}
              <Input
                autoFocus
                value={creating}
                readOnly={making}
                maxLength={60}
                enterKeyHint="done"
                placeholder={t('library.folderName')}
                aria-label={t('library.newFolder')}
                onChange={(event) => setCreating(event.target.value)}
                onBlur={() => endCreate(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') endCreate(true);
                  if (event.key === 'Escape') endCreate(false);
                }}
                className="h-9 my-0.5 ms-1 flex-1 min-w-0"
              />
              <button
                type="button"
                disabled={making}
                // Pressing the tick would take the focus from the field before
                // the tick is clicked, and leaving the field drops the name.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => endCreate(true)}
                aria-label={t('common.save')}
                title={t('common.save')}
                className="p-1.5 shrink-0 rounded-lg text-primary hover:bg-muted transition-colors disabled:opacity-30"
              >
                <Check className="h-4 w-4" />
              </button>
            </div>
          )}
          {refused?.id === NEW_FOLDER && (
            <div className="px-3 pb-2">
              <ErrorNotice code={refused.code} />
            </div>
          )}
        </div>
      </AnchoredMenu>
    </>
  );
}

/**
 * 1.45 — where a document is filed: "ללא תיקייה" first, which takes it out of its
 * folder, and then the folders. A press on one does it; a press on the one the
 * document is already in sends nothing.
 */
function MoveToFolderDialog({
  document,
  folders,
  loading,
  onPick,
  onClose,
}: {
  document: LibraryDocument | null;
  folders: LibraryFolder[];
  loading: boolean;
  onPick: (folder: LibraryFolder | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const current = document?.folderId ?? null;

  const choice = (folder: LibraryFolder | null, label: string) => {
    const here = (folder?.id ?? null) === current;
    return (
      <button
        type="button"
        disabled={loading}
        aria-current={here ? 'true' : undefined}
        onClick={() => (here ? onClose() : onPick(folder))}
        className={cn(menuItem, 'rounded-lg hover:bg-muted disabled:opacity-60', here && 'text-primary')}
      >
        <span className="truncate">{label}</span>
        {here && <Check className="ms-auto h-4 w-4 shrink-0" aria-hidden />}
      </button>
    );
  };

  return (
    <Modal open={Boolean(document)} title={t('library.moveToFolder')} onClose={onClose}>
      <div className="space-y-1">
        {choice(null, t('library.noFolder'))}
        {folders.map((folder) => (
          <div key={folder.id}>{choice(folder, folder.name)}</div>
        ))}
      </div>
      {folders.length === 0 && <p className="pt-2 text-sm text-muted-foreground">{t('library.moveNoFolders')}</p>}
    </Modal>
  );
}
